import { filter, forEach, map, reduce } from 'lodash';

import makeLinkedTree from '../makeLinkedTree';

const reduceTreeWithFilterMatch = linkedTreeNodes =>
  filter(
    map(linkedTreeNodes, node =>
      node.filterMatch
        ? { ...node, children: reduceTreeWithFilterMatch(node.children) }
        : null,
    ),
    node => !!node,
  );

const collectEntities = linkedTreeNodes =>
  reduce(
    linkedTreeNodes,
    (acc, { entity, children }) => [
      ...acc,
      entity,
      ...collectEntities(children),
    ],
    [],
  );

const applyPredicate = (
  linkedTreeNodes,
  predicate,
  ancestors = false,
  filters,
) =>
  reduce(
    linkedTreeNodes,
    (anyNodePassed, node) => {
      const self = predicate(node.entity, filters);

      const childrenMatch = applyPredicate(
        node.children,
        predicate,
        ancestors || self,
        filters,
      );

      node.predicateMatch = { self, ancestors, childrenMatch };

      return anyNodePassed || self || childrenMatch;
    },
    false,
  );

const applyFilter = (linkedTreeNodes, options) => {
  const {
    getNodeId,
    includeParents,
    includeChildren,
    visibleEntityIds,
  } = options;

  forEach(linkedTreeNodes, node => {
    const { self, ancestors, childrenMatch } = node.predicateMatch;

    if (!self && !childrenMatch && !includeChildren) {
      node.filterMatch = false;
    } else {
      node.filterMatch =
        self ||
        (includeParents && childrenMatch) ||
        (includeChildren && ancestors);
    }

    if (visibleEntityIds && self) {
      visibleEntityIds.add(getNodeId(node.entity));
    }

    applyFilter(node.children, options);
  });
};

const filterTree = (
  entities,
  predicates,
  { getNodeId, getNodeParentId } = {},
  filters,
) => {
  let linkedTreeRoots = filter(
    makeLinkedTree(entities, {
      getNodeId,
      getNodeParentId,
    }),
    node => !node.parent,
  );

  const visibleEntityIds = new Set();

  forEach(
    filter(predicates, ({ predicate }) => !!predicate),
    ({
      includeParents = true,
      includeChildren,
      collectVisibleIds = true,
      predicate,
    }) => {
      applyPredicate(linkedTreeRoots, predicate, false, filters);

      applyFilter(linkedTreeRoots, {
        getNodeId,
        includeParents,
        includeChildren,
        visibleEntityIds: collectVisibleIds ? visibleEntityIds : null,
      });

      linkedTreeRoots = reduceTreeWithFilterMatch(linkedTreeRoots);
    },
  );

  return { entities: collectEntities(linkedTreeRoots), visibleEntityIds };
};

export default filterTree;
